/*
 * Brewer / Espresso Machine Quiz
 * Spec: gear-content/brewer-quiz/TREE.md
 *
 * Answers set hard FILTERS (eliminate products) and build a WEIGHT vector
 * (rank the survivors). We score every survivor, then pick three that DIFFER
 * on a high-weight axis so the buyer sees a real tradeoff rather than three
 * near-identical machines.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------- axes ---

  var NUMERIC_RANGE = 4; // 1..5 scales

  var CAPACITY_ORDER = { single: 1, smallbatch: 2, carafe: 3, largecarafe: 4 };

  var AXES = {
    automation: { type: 'num' },
    control: { type: 'num' },
    skill: { type: 'num' },
    tech: { type: 'num' },
    warmup: { type: 'num' },
    footprint: { type: 'num' },
    aesthetics: { type: 'num' },
    maint_ease: { type: 'num' },
    durability: { type: 'num' },
    capacity: { type: 'ord' },
    boiler: { type: 'boiler' },
    plumbable: { type: 'bool' },
    price: { type: 'price' }
  };

  // "best for ___" hats, per spec §2 step 3. `dir` is which end of the axis
  // earns the hat: 'high' = a big number wins, 'low' = a small number wins.
  var HATS = [
    { axis: 'maint_ease', dir: 'high', label: 'Lowest fuss' },
    { axis: 'control', dir: 'high', label: 'Most control' },
    { axis: 'price', dir: 'low', label: 'Best value' },
    { axis: 'durability', dir: 'high', label: 'Built to last' },
    { axis: 'aesthetics', dir: 'high', label: 'The showpiece' },
    { axis: 'capacity', dir: 'high', label: 'Best for a crowd' },
    { axis: 'skill', dir: 'low', label: 'Most forgiving' },
    { axis: 'warmup', dir: 'high', label: 'Fastest mornings' },
    // Named for what the machine has, not "Smartest" (Dean, 2026-10-13: "tech"
    // says nothing about what someone gets). See techLabel().
    { axis: 'tech', dir: 'high', label: null }
  ];

  // Compare-strip vocabulary. Index 0 is unused so value 1..5 maps directly.
  // Every step has to be legibly different from the ones either side of it.
  // These are read side by side in the compare table, so near-synonyms are
  // worse than useless: "Regular cleaning" sat next to "Moderate" and said
  // nothing, aesthetics had "Plain" twice, footprint had "Large" then "Big".
  var SCALE_WORDS = {
    automation: [, 'Fully manual', 'Mostly manual', 'Semi-auto', 'Push-button', 'One touch'],
    control: [, 'None', 'Basic', 'Temperature', 'Temperature and more', 'Flow profiling'],
    warmup: [, 'Long warm-up', 'Slow to heat', 'A few minutes', 'Quick', 'Ready instantly'],
    skill: [, 'None', 'Easy to learn', 'Some practice', 'Demanding', 'Steep curve'],
    maint_ease: [, 'Backflush and descale', 'Weekly deep clean', 'Occasional descale', 'Quick daily rinse', 'Rinse and go'],
    durability: [, 'Basic', 'Light-duty', 'Solid', 'Tough', 'Built to last'],
    aesthetics: [, 'Utilitarian', 'Plain', 'Neat', 'Handsome', 'Showpiece'],
    tech: [, 'Buttons and dials', 'Temperature display', 'Settings screen', 'Menu screen', 'Touchscreen or app'],
    footprint: [, 'Very large', 'Large', 'Average', 'Compact', 'Tiny']
  };

  var BOILER_ORDER = { single: 1, thermoblock: 2, hx: 3, dual: 4 };

  var CAPACITY_WORDS = {
    single: 'One cup',
    smallbatch: 'Small batch',
    carafe: 'Carafe',
    largecarafe: 'Large carafe'
  };

  // ------------------------------------------------------------ questions ---

  // Effects grammar:
  //   filter: { family: [..], milk: 'auto', grinder_builtin: 1, simul_steam: 1,
  //             capacity: [..], maxPrice: n }
  //   weight: [{ axis, w, t }]   t is a 1..5 target, a capacity name, or 'low'
  //   next:   step id to jump to
  // Answer photos. Q1 uses the coffee quiz's own brew photos; the rest are real
  // photos from our product pages, chosen for showing the machine in use.
  var IMG = {
    filter: 'https://cdn.shopify.com/s/files/1/0677/2880/1852/files/hario_v60-02-ceramic-dripper_white_pourover_2.jpg',
    espresso: 'https://cdn.shopify.com/s/files/1/0677/2880/1852/files/IMG_6418.jpg',
    superauto: 'https://cdn.shopify.com/s/files/1/0677/2880/1852/files/JU15496_machine_espresso_automatique_jura_ena8_fullmetropolitan_black__1_1500x_jpg.webp',
    semiauto: 'https://cdn.shopify.com/s/files/1/0677/2880/1852/files/edika_lelit_bianca_pl162t_machine_espresso_manuelle_coffee_spout_1500x_jpg.webp',
    lever: 'https://cdn.shopify.com/s/files/1/0677/2880/1852/files/flair_58_plus_2_lifestyle_7_small_c5243cc6-4828-41c4-95d3-fc49fde64576.jpg',
    builtin: 'https://cdn.shopify.com/s/files/1/0677/2880/1852/files/edika_lelit_kate_pl182t_machine_espresso_manuelle_1500x_jpg.webp',
    separate: 'https://cdn.shopify.com/s/files/1/0677/2880/1852/files/GF64.jpg'
  };

  function buildSteps(cfg) {
    return {
      Q1: {
        title: 'What coffee do you want to make?',
        options: [
          {
            label: 'Espresso drinks',
            sub: 'Lattes, cappuccinos, americanos, straight shots.',
            img: [IMG.espresso],
            filter: { family: ['superauto', 'prosumer', 'lever'] },
            next: 'XESP'
          },
          {
            label: 'Filter coffee',
            sub: 'Drip, pour-over, AeroPress or moka pot.',
            img: [IMG.filter],
            filter: { family: ['batchdrip', 'autopour', 'manualpour', 'immersion', 'moka'] },
            next: 'F2'
          },
          {
            label: 'Both espresso and filter',
            img: [IMG.filter, IMG.espresso],
            filter: { family: ['superauto', 'prosumer', 'lever'] },
            weight: [{ axis: 'automation', w: 1, t: 5 }],
            next: 'XESP'
          },
          { label: 'Not sure yet', next: 'TRIAGE' }
        ]
      },

      TRIAGE: {
        title: 'What would make your coffee better tomorrow?',
        options: [
          {
            label: 'Café drinks at home',
            filter: { family: ['superauto', 'prosumer', 'lever'] },
            next: 'XESP'
          },
          {
            label: 'Better filter coffee',
            sub: 'Drip, pour-over, AeroPress or moka pot.',
            filter: { family: ['batchdrip', 'autopour', 'manualpour', 'immersion', 'moka'] },
            next: 'F2'
          },
          {
            label: 'Something faster and easier',
            filter: { family: ['superauto', 'prosumer', 'lever'] },
            weight: [{ axis: 'automation', w: 1, t: 5 }],
            next: 'XESP'
          }
        ]
      },

      // First question on the espresso arm, and deliberately the one a shop
      // assistant would open with. It carries NO WEIGHTS on purpose.
      //
      // Both halves of that are Dean's, and they are not in tension. Experience
      // does not predict desired control: a beginner can love tinkering and want
      // the most control, and when this question weighted `skill` it pushed
      // exactly that person toward forgiving machines. So it no longer scores.
      // It stays because it is how a person would start the conversation, and
      // because being asked makes the recommendation feel chosen rather than
      // computed. What the buyer WANTS is asked straight after, by E1 to E5,
      // and that is what moves the ranking.
      //
      // Do not "fix" this by adding weights. That is the bug it used to have.
      XESP: {
        title: 'How much experience do you have with espresso machines?',
        options: [
          { label: 'None. I have never pulled a shot.', level: 'none', next: 'E1' },
          { label: 'Some. I have used a machine at home or at work.', level: 'some', next: 'E1' },
          { label: 'A lot. I dial in grind and ratio and like to tinker.', level: 'lots', next: 'E1' }
        ]
      },

      // ---- espresso arm ----
      E1: {
        title: 'How do you want to prepare milk-based drinks?',
        why: 'Automatic frothers texture milk to a set recipe, the same way every time. ' +
          'A steam wand gives you control over microfoam, which is what latte art needs, but it takes practice.',
        options: [
          {
            label: 'Automatic, one touch',
            filter: { milk: 'auto' },
            relax: 'one-touch milk',
            weight: [{ axis: 'automation', w: 2, t: 5 }],
            next: 'E2'
          },
          {
            label: "I'll steam it myself",
            sub: 'Latte art is on the table.',
            weight: [{ axis: 'control', w: 2, t: 4 }, { axis: 'skill', w: 1, t: 4 }],
            next: 'E2'
          },
          {
            label: 'No preference, as long as the milk is good',
            weight: [{ axis: 'automation', w: 1, t: 4 }],
            next: 'E2'
          },
          {
            // No milk filter and no weight: every machine stays in play, and we
            // stop steaming from counting for or against the recommendation.
            label: 'I drink it black',
            sub: 'Straight espresso and americanos.',
            next: 'E2'
          }
        ]
      },

      E2: {
        // Dosing and tamping only. Naming grinding here was the error that put
        // this and the grinder question out of order: it pre-answered a question
        // that only has an answer for one of the three machine types. All 16
        // super-automatics grind and neither lever does, so for them the grinder
        // is settled; for semi-automatics it is genuinely open, which is why
        // they are the only ones asked.
        //
        // Only one semi-automatic we stock has a built-in grinder today, the
        // Lelit Kate. That is a gap in the catalogue, not a reason to drop the
        // question: De'Longhi and Breville both make semi-automatics with one
        // and Dean intends to carry them (2026-08-07). Do not delete E2G on the
        // grounds that it currently returns a single machine.
        title: 'Who doses and tamps the coffee?',
        // The experience answer earns its place here rather than in the maths.
        // It does not move the ranking, it decides which advice we give and
        // lets us answer back what the buyer just told us.
        recommendsFor: {
          none: 'A super-automatic, to start. You said you have never pulled a shot, and ' +
            'a machine that grinds, doses and tamps for you is the one that gets a good ' +
            'drink out on a weekday morning. Nothing stops you moving up later.',
          some: 'A semi-automatic. You have used a machine before, and dosing and tamping ' +
            'yourself is the step that most changes what ends up in the cup.',
          lots: 'A semi-automatic. You said you like to tinker, so a super-automatic would ' +
            'take away the parts you enjoy and cap how good the shot can get.'
        },
        why: 'The terms describe how much of the shot is yours. A super-automatic grinds, doses, brews ' +
          'and often froths at the press of a button. A semi-automatic leaves the dosing and tamping to ' +
          'you, and you start and stop the shot yourself. A manual lever adds one more step: you work ' +
          'the brew pressure by hand, which is where the Flairs sit. None of this says anything about ' +
          'the grinder, which we ask about next.',
        options: [
          {
            label: 'Super-automatic',
            sub: 'It grinds and brews at the touch of a button.',
            img: [IMG.superauto],
            // Flagged "Stillwater recommends" for these experience answers.
            recommendedFor: ['none'],
            filter: { family: ['superauto'] },
            weight: [{ axis: 'automation', w: 2, t: 5 }],
            next: 'E4'
          },
          {
            label: 'Semi-automatic',
            sub: 'I dose and tamp, then start and stop the shot myself.',
            img: [IMG.semiauto],
            recommendedFor: ['some', 'lots'],
            filter: { family: ['prosumer'] },
            weight: [{ axis: 'control', w: 1, t: 4 }],
            next: 'E2G'
          },
          {
            label: 'Manual lever',
            sub: 'I pull the shot by hand and work the pressure myself.',
            img: [IMG.lever],
            filter: { family: ['lever'] },
            weight: [{ axis: 'control', w: 2, t: 5 }, { axis: 'skill', w: 1, t: 4 }],
            next: 'E3'
          },
          {
            // Skips the grinder question and the temperature-and-flow one, and
            // carries no weight. Both were wrong for this answer: it sent the
            // person who just said they do not know into the two most technical
            // steps in the quiz, and it weighted control toward 4, guessing a
            // preference for hands-on machines on their behalf. Saying "show me
            // what fits" should leave every family in play and let the drinks,
            // mornings, budget and space answers do the choosing.
            label: 'Not sure yet. Show me what fits.',
            next: 'E4'
          }
        ]
      },

      E2G: {
        title: 'Do you want the grinder built into the machine, or separate?',
        why: 'This is independent of the machine type. Plenty of semi-automatics have a grinder in the ' +
          'body, and a super-automatic always does. A separate grinder is a component you can upgrade ' +
          'or replace on its own.',
        recommends: 'A separate grinder. For the same total spend and about the same counter space, ' +
          'you usually get better grinding than a built-in.',
        options: [
          {
            label: 'Built in',
            sub: 'One machine on the counter.',
            img: [IMG.builtin],
            filter: { grinder_builtin: 1 },
            next: 'E3'
          },
          {
            // "Separate is fine" stated a tolerance, not a preference, and was
            // indistinguishable from the no-preference answer sitting under it.
            label: 'Separate',
            sub: 'I choose the grinder. We will flag which machines need one.',
            img: [IMG.separate],
            recommended: true,
            // A filter, not only a weight (Dean, 2026-10-21): as a weight alone,
            // a La Specialista with its built-in grinder could still win for
            // someone who asked for a separate one.
            filter: { grinder_builtin: 0 },
            relax: 'a separate grinder',
            weight: [{ axis: 'control', w: 1, t: 4 }],
            next: 'E3'
          },
          {
            label: 'No preference',
            next: 'E3'
          }
        ]
      },

      E3: {
        title: 'How much control do you want over temperature and flow?',
        why: 'Temperature stability and how water moves through the puck are the two things that change ' +
          'a shot most. A pressure gauge tells you what is happening; flow control and pre-infusion let ' +
          'you change it.',
        options: [
          {
            label: 'None. Set it once and let it pull good shots.',
            weight: [{ axis: 'control', w: 1, t: 2 }, { axis: 'skill', w: 1, t: 2 }],
            next: 'E4'
          },
          {
            label: 'Temperature. I want to dial the shot in.',
            // Control alone. A tech weight here once ranked a touchscreen as
            // better temperature control, which it is not (2026-10-13).
            weight: [{ axis: 'control', w: 2, t: 3 }],
            next: 'E4'
          },
          {
            label: 'Temperature and flow. Profiling and pre-infusion.',
            weight: [{ axis: 'control', w: 3, t: 5 }],
            next: 'E4'
          }
        ]
      },

      // The boiler is the mechanism, milk is the situation. Steaming is what
      // makes a single boiler wait, so this asks about milk drinks rather than
      // drinks in general: someone who takes it black can run a single boiler
      // all morning without noticing. The answers score on the boiler.
      E4: {
        title: 'How many milk drinks do you make back to back?',
        why: 'Steaming milk and pulling a shot at the same time needs either a heat exchanger or ' +
          'two boilers. A single boiler switches between the two and makes you wait in between, ' +
          'which only bites when there is milk to steam.',
        options: [
          {
            label: 'One, or none. I am not steaming much milk.',
            // A single boiler is genuinely fine here, so nothing is weighted.
            next: 'E5'
          },
          {
            label: 'Two or three, without waiting between them',
            filter: { simul_steam: 1 },
            weight: [
              { axis: 'warmup', w: 1, t: 4 },
              // Two boilers beat a heat exchanger at this and both beat one
              // boiler, so the ranking says so rather than treating every
              // machine that clears the filter as equal.
              { axis: 'boiler', w: 2, t: 4 }
            ],
            next: 'E5'
          },
          {
            label: 'A round for the whole house',
            filter: { capacity: ['carafe', 'largecarafe'] },
            relax: 'making a round for the whole house',
            weight: [
              { axis: 'capacity', w: 2, t: 'largecarafe' },
              { axis: 'plumbable', w: 1, t: 1 },
              { axis: 'boiler', w: 2, t: 4 }
            ],
            next: 'E5'
          }
        ]
      },

      E5: {
        title: 'Ready in seconds, or worth the wait?',
        why: 'Thermoblock and bean-to-cup machines are ready almost immediately. A traditional group ' +
          'head holds a lot of brass and needs time to come up to temperature.',
        options: [
          { label: 'Ready in seconds', weight: [{ axis: 'warmup', w: 2, t: 5 }], next: 'E6' },
          {
            label: "Happy to let it warm up for a better shot",
            weight: [{ axis: 'warmup', w: 1, t: 2 }, { axis: 'control', w: 1, t: 4 }],
            next: 'E6'
          },
          { label: 'No preference', next: 'E6' }
        ]
      },

      E6: budgetStep(cfg.espressoBands, 'E7'),

      // Retitling this to counter space alone dropped the looks question, which
      // is the one people answer instantly even though it resists measuring.
      // It asks both, because the answers were always about both: footprint if
      // it has to disappear, appearance if it gets to sit out.
      E7: {
        title: 'Should it tuck away, or do you want it on show?',
        options: [
          {
            label: 'Tuck away. Counter space matters more than looks.',
            weight: [{ axis: 'footprint', w: 2, t: 5 }],
            next: 'RESULTS'
          },
          {
            label: 'On show. I want a machine I like looking at.',
            weight: [{ axis: 'aesthetics', w: 2, t: 5 }],
            next: 'RESULTS'
          },
          { label: 'No preference', next: 'RESULTS' }
        ]
      },

      // ---- filter arm ----
      // Last on the arm now: how much you brew only reorders the survivors, and
      // it reads oddly before we know whether we are talking about a carafe
      // machine or a pour-over cone.
      F1: {
        title: 'Brewing for you, or for a crowd?',
        options: [
          { label: 'Just me, a cup or two', weight: [{ axis: 'capacity', w: 1, t: 'single' }], next: 'RESULTS' },
          { label: 'A household, a pot at a time', weight: [{ axis: 'capacity', w: 2, t: 'carafe' }], next: 'RESULTS' },
          {
            label: 'Big batches',
            filter: { capacity: ['carafe', 'largecarafe'] },
            relax: 'brewing big batches',
            weight: [{ axis: 'capacity', w: 2, t: 'largecarafe' }],
            next: 'RESULTS'
          }
        ]
      },

      F2: {
        // Asked as how they want to brew, pour-over first and automatic last, so
        // the two hands-on methods set the context for what "automatic" means.
        // Dean, 2026-10-08.
        title: 'How do you want to brew your coffee?',
        why: 'Steeping and pouring are different jobs. With an AeroPress the water sits with the ' +
          'grounds and time does the work, so it is hard to get wrong. A moka pot pushes water up ' +
          'through the grounds on the stove. With pour-over you control the water yourself, and how ' +
          'you pour changes how the cup tastes.',
        options: [
          {
            label: 'Pour over',
            filter: { family: ['manualpour', 'immersion'] },
            weight: [{ axis: 'control', w: 2, t: 4 }],
            next: 'F3'
          },
          {
            label: 'Steep and press, like an AeroPress',
            filter: { family: ['immersion'] },
            weight: [{ axis: 'skill', w: 2, t: 1 }],
            next: 'F1'
          },
          {
            // A moka pot does not steep: steam pressure pushes the water up
            // through the grounds. It had been filed under steep and press.
            label: 'Stovetop, like a moka pot',
            filter: { family: ['moka'] },
            weight: [{ axis: 'skill', w: 2, t: 1 }],
            next: 'F1'
          },
          {
            label: 'Automatic. Just add water and ground beans.',
            filter: { family: ['batchdrip', 'autopour'] },
            weight: [{ axis: 'automation', w: 2, t: 4 }],
            next: 'F4'
          }
        ]
      },

      // Only reached by people who said they want to pour by hand, and it asks
      // something anyone can answer without interpreting it. It separates a
      // forgiving dripper from one that rewards a steady hand, which is the one
      // thing F2 cannot tell us.
      // Asks the decision it is actually making. This step exists to choose
      // between a forgiving brewer and one that rewards technique: answer it one
      // way and the AeroPress leads, the other and the V60 does. Two earlier
      // versions proxied that through a habit ("how much do you want to control
      // the brew", then "do you weigh your coffee and water") and both had to
      // invent vocabulary to stand in for the real question. Ask the real one.
      F3: {
        title: 'Should the brewer be forgiving, or reward practice?',
        options: [
          {
            label: 'Forgiving. I want a good cup without thinking about it.',
            weight: [{ axis: 'skill', w: 2, t: 1 }],
            next: 'F1'
          },
          {
            label: 'A bit of both',
            weight: [{ axis: 'skill', w: 1, t: 3 }],
            next: 'F1'
          },
          {
            label: 'Reward practice. I want to get better at this.',
            weight: [{ axis: 'skill', w: 1, t: 4 }, { axis: 'control', w: 1, t: 4 }],
            next: 'F1'
          }
        ]
      },

      F4: budgetStep(cfg.filterBands, 'F1'),

    };
  }

  function budgetStep(bandsRaw, next) {
    var ceilings = String(bandsRaw || '')
      .split(',')
      .map(function (n) { return parseFloat(String(n).trim()); })
      .filter(function (n) { return isFinite(n) && n > 0; })
      .sort(function (a, b) { return a - b; });

    var options = [];
    var previous = 0;
    ceilings.forEach(function (ceiling, i) {
      options.push({
        label: previous === 0 ? 'Under ' + money(ceiling) : money(previous) + ' – ' + money(ceiling),
        filter: {
          maxPrice: ceiling,
          // The band's lower edge (Dean, 2026-10-21). Without it "$2,000 –
          // $3,500" admitted a $699 machine, and nothing in the ranking held
          // a cheap machine back. One pick below it may still fill the third
          // slot; see `below` in score().
          minPrice: previous || null,
          // The best fit never breaks the ceiling, but one alternate is allowed
          // to reach into the next band — often the "spend a bit more and it
          // solves the thing you compromised on" card. The last bounded band has
          // no next ceiling, so give it a proportional stretch instead of
          // opening it up to the whole catalogue.
          stretchPrice: ceilings[i + 1] || Math.round(ceiling * 1.5)
        },
        next: next
      });
      previous = ceiling;
    });
    // The top band has a floor too, or "$3,500 and up" would admit a $699
    // machine just as the bounded bands did.
    options.push({ label: money(previous) + ' and up', filter: { minPrice: previous }, next: next });

    return {
      title: "What's your budget?",
      // Was "We never recommend above it", which was simply untrue: the best
      // fit is capped, but one alternate is deliberately allowed a band up as
      // the "spend a bit more and it fixes the compromise" card.
      // Says outright that the figure is the machine alone. The old note asked
      // the buyer to set aside part of this budget for a grinder, which left it
      // ambiguous whether the number they typed was meant to cover both, and
      // made the filtering look like arithmetic they could not see.
      sub: 'The machine only. Your best match stays inside it. One alternative may cost more, and one may cost less.',
      options: options
    };
  }

  // Show the price the shopper will actually be charged. Rounding to whole
  // dollars turned a real $999.99 into "$1,000", which both overstates it and
  // reads as a different price point to someone who just capped their budget at
  // a thousand. Whole-dollar prices still print clean, without a trailing .00.
  function money(n) {
    var value = Number(n);
    var whole = Math.round(value * 100) % 100 === 0;
    return '$' + value.toLocaleString('en-CA', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    });
  }

  // ---------------------------------------------------------------- state ---

  function Quiz(root) {
    this.root = root;
    this.cfg = readConfig(root);
    this.steps = buildSteps(this.cfg);
    this.stage = root.querySelector('[data-bq-stage]');
    this.nav = root.querySelector('[data-bq-nav]');
    this.backBtn = root.querySelector('[data-bq-back]');
    this.progressFill = root.querySelector('[data-bq-progress-fill]');
    this.products = null;
    this.history = [];
    this.current = this.cfg.coverHeading ? 'COVER' : 'Q1';

    var self = this;
    this.backBtn.addEventListener('click', function () { self.back(); });
    this.fit();
    window.addEventListener('resize', function () {
      self.fit();
      self.placeSeal();
    });

    this.load();
  }

  function readConfig(root) {
    var node = root.querySelector('[data-bq-config]');
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return {};
    }
  }

  Quiz.prototype.load = function () {
    var self = this;

    if (this.cfg.quizUrl) {
      this.renderTeaser();
      return;
    }

    // The cover and the questions are built from the config alone, so they
    // render now. Until 2026-10-07 the whole quiz, cover included, waited behind
    // the matrix and 54 product fetches, and the start button took about 3.4 s
    // to appear on a phone. The data loads while the buyer answers, and only the
    // results wait for it (see showResults).
    //
    // The live product list does not depend on the matrix, so both requests
    // start together. The section preloads both with the page.
    var live = this.fetchLiveList();
    this.ready = fetch(this.cfg.matrixUrl)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var all = data.products || [];
        // Prices, stock and images come off the products themselves, never out
        // of the matrix. The matrix carries the scoring axes and a handle; the
        // storefront carries what a shopper would actually be charged.
        return live.then(function (byHandle) {
          return self.loadLive(all, byHandle);
        }).then(function () {
          self.products = all.filter(function (p) {
            return p.available && p.price != null;
          });
        });
      });
    // Swallow here so an early failure is not an unhandled rejection; the
    // results step reports it.
    this.ready.catch(function () {});

    // The cover is in the page HTML and paints before this script runs. A tap
    // on Start in that gap is recorded by the inline script in the section;
    // honour it here instead of making the buyer tap again.
    if (this.current === 'COVER' && this.root.getAttribute('data-bq-queued') === 'start') {
      this.root.removeAttribute('data-bq-queued');
      return this.start();
    }
    this.render();
  };

  // The live storefront list for every machine in one request:
  // /collections/{handle}/products.json, up to 250 products. Until 2026-10-07
  // this was one /products/{handle}.js request per machine, 54 in all, which on
  // a throttled phone took 3.6 s against 1.3 s for the single list, and much
  // longer while the page's images competed for the same connection. Resolves
  // to a map of handle -> product, or an empty map, in which case loadLive
  // falls back to the per-product requests.
  Quiz.prototype.fetchLiveList = function () {
    if (!this.cfg.liveListUrl) return Promise.resolve({});
    return fetch(this.cfg.liveListUrl, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var byHandle = {};
        ((data && data.products) || []).forEach(function (p) { byHandle[p.handle] = p; });
        return byHandle;
      })
      .catch(function () { return {}; });
  };

  // The results screen waits on the product data. It shows the coffee quiz's
  // matching step, which ticks a check every 550 ms and moves on after the
  // fourth tick, and waits longer only if the data is still loading.
  var MATCH_TICK = 550;
  var MATCH_MS = MATCH_TICK * 4;

  Quiz.prototype.showResults = function () {
    var self = this;
    var token = {};
    this.pendingResults = token;
    this.renderMatching();
    var minWait = new Promise(function (resolve) { setTimeout(resolve, MATCH_MS); });
    Promise.all([this.ready, minWait])
      .then(function () {
        if (self.pendingResults !== token || self.current !== 'RESULTS') return;
        self.renderResults();
      })
      .catch(function () {
        if (self.pendingResults !== token) return;
        self.screen('cq2-q',
          '<h2 class="cq2-h2">We could not load the machine data</h2>' +
          '<p class="cq2-sub"><a href="#ProductGridContainer">Browse the machines</a> in the meantime.</p>', true);
      });
  };

  Quiz.prototype.renderMatching = function () {
    this.screen('cq2-matching',
      '<div class="cq2-spinner" aria-hidden="true"></div>' +
      '<h2 class="cq2-h2">Finding your match</h2>' +
      '<ul class="cq2-checks">' +
      '<li>Checking what you told us</li>' +
      '<li>Comparing the machines</li>' +
      '<li>Picking three that differ</li></ul>', false);
    var items = this.stage.querySelectorAll('.cq2-checks li');
    Array.prototype.forEach.call(items, function (li, i) {
      setTimeout(function () { li.classList.add('done'); }, MATCH_TICK * (i + 1));
    });
  };

  // Pull price, stock and image from each product's own storefront record.
  //
  // /products/{handle}.js is the product page's data, served live by Shopify.
  // Anything baked into the deployed matrix is a snapshot that silently rots:
  // it had the Magnifica Evo at $649 against a real $849.99 for two months.
  // Prices are in cents here, hence the /100.
  //
  // A product that will not resolve gets no price and is dropped rather than
  // shown at a guessed one. There is deliberately no manual-price fallback: a
  // machine with no storefront page cannot be linked to either, so a card for
  // one would send the buyer to a 404. Unpublished means absent, on purpose.
  //
  // Most machines come out of the one list request (fetchLiveList). Only a
  // handle missing from it, say a product added to the matrix but not to the
  // collection, costs its own /products/{handle}.js request.
  Quiz.prototype.loadLive = function (products, byHandle) {
    var LIMIT = 8; // polite concurrency; ~250ms for 20 same-origin
    byHandle = byHandle || {};

    // The two sources differ in shape: products.json has prices as dollar
    // strings ("849.99") and an images list; .js has cents and featured_image.
    function apply(entry, p, dollars, productImage) {
      var variants = p.variants || [];
      var inStock = variants.filter(function (v) { return v.available; });
      var pool = inStock.length ? inStock : variants;
      var v = pool.slice().sort(function (a, b) { return dollars(a.price) - dollars(b.price); })[0];
      if (!v) return;
      entry.price = dollars(v.price);
      entry.available = !!v.available;
      entry.variant_id = v.id;
      entry.product_id = p.id;
      if (v.featured_image && v.featured_image.src) entry.image = v.featured_image.src;
      else if (productImage) entry.image = productImage;
      if (p.vendor) entry.vendor = p.vendor;
    }

    var queue = [];
    products.forEach(function (entry) {
      // A machine we do not sell has no product of ours to read. Its stored
      // Canadian price and photo come in the matrix, from the same pick library
      // entry a gear article renders (build_matrix.py).
      if (entry.sold === 0) { entry.available = true; return; }
      var p = byHandle[entry.handle];
      if (!p) { queue.push(entry); return; }
      apply(entry, p, Number, p.images && p.images[0] && p.images[0].src);
    });

    function hydrate(entry) {
      return fetch('/products/' + entry.handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return;
          apply(entry, p, function (cents) { return cents / 100; }, p.featured_image);
        })
        .catch(function () { /* leave price null; the filter above drops it */ });
    }

    function worker() {
      var next = queue.shift();
      if (!next) return Promise.resolve();
      return hydrate(next).then(worker);
    }

    var lanes = [];
    for (var i = 0; i < Math.min(LIMIT, queue.length); i++) lanes.push(worker());
    return Promise.all(lanes);
  };

  // The experience answer carries no weight, so this is the only place it does
  // any work: it picks which advice we give and lets that advice answer back
  // what the buyer told us a moment ago.
  Quiz.prototype.answeredLevel = function () {
    for (var i = this.history.length - 1; i >= 0; i--) {
      if (this.history[i].option.level) return this.history[i].option.level;
    }
    return null;
  };

  Quiz.prototype.back = function () {
    // Q1 is the first question, but it is not the first screen: the cover is.
    // Back there returns to the cover instead of being a dead button.
    if (!this.history.length) {
      if (this.current === 'Q1' && this.cfg.coverHeading) {
        this.current = 'COVER';
        this.render();
      }
      return;
    }
    var last = this.history.pop();
    this.current = last.step;
    // `last.step` is the step we are returning TO, so sending Q1 to the cover
    // here skipped the first question entirely: Back from question two landed
    // on START THE QUIZ and cost a second click to get the answers again.
    // Q1 renders with the cover above it and hides its own nav when the
    // history is empty, which is the behaviour that redirect was reaching for.
    this.render();
  };

  Quiz.prototype.choose = function (option) {
    this.history.push({ step: this.current, option: option });
    this.track('brewer_quiz_answered', { step: this.current, answer: option.label });

    this.current = option.next;
    this.render();
  };


  // Shared with the wishlist page (assets/brewer-wishlist.js), which reads it.
  var SAVE_KEY = 'stillwater:wishlist-token';

  // ------------------------------------------------------------- wishlist ---
  //
  // The save heart: gear-content/brewer-quiz/SAVE-SPEC.md, drawn on the Claude
  // Design board "Save heart" (artifact 5CEWazoGFR56JcKe9bQTyM), copy approved
  // by Dean 2026-10-08. A heart saves to a list held in this page at once. The
  // list is kept only once an email is given: POST /wishlist stores it and the
  // Klaviyo flow "Brewer quiz wishlist" emails the link to the wishlist page.
  // With a list already on file (a token on this device, or a signed-in
  // customer's list) the heart adds to it directly and nothing asks for an email.

  function loadToken() {
    try { return window.localStorage.getItem(SAVE_KEY) || ''; } catch (e) { return ''; }
  }

  function storeToken(token) {
    try { window.localStorage.setItem(SAVE_KEY, token); } catch (e) { /* private mode */ }
  }

  function shopifyCustomerId() {
    var page = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page;
    return page && page.customerId ? String(page.customerId) : '';
  }

  Quiz.prototype.wishlistInit = function () {
    if (this.wl || !this.cfg.saveUrl) return;
    var self = this;
    this.wl = { saved: {}, token: loadToken(), state: 'none', ask: false, url: '', error: '' };
    if (this.wl.token) this.wl.state = 'done';
    var cid = shopifyCustomerId();
    if (this.wl.token || !cid) return;
    fetch(this.cfg.saveUrl.replace(/\/$/, '') + '/wishlist/by-customer/' + encodeURIComponent(cid))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.ok || !d.token) return;
        storeToken(d.token);
        self.wl.token = d.token;
        self.wl.state = 'done';
        (d.items || []).forEach(function (it) { self.wl.saved[it.handle] = true; });
        if (self.current === 'RESULTS' && self.result && !self.wl.ask) self.renderResultTab(false);
      })
      .catch(function () { /* the heart still works; it asks for an email instead */ });
  };

  // The service allows one save a second per visitor, so a second heart pressed
  // straight after the first waits a second and tries once more.
  Quiz.prototype.wlPost = function (path, body) {
    var url = this.cfg.saveUrl.replace(/\/$/, '') + path;
    function send() {
      return fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    }
    return send()
      .then(function (r) {
        if (r.status !== 429) return r;
        return new Promise(function (resolve) { setTimeout(resolve, 1100); }).then(send);
      })
      .then(function (r) {
        return r.json().then(function (d) { return { ok: r.ok && d && d.ok, data: d || {} }; });
      })
      .catch(function () { return { ok: false, data: {} }; });
  };

  Quiz.prototype.heartClick = function (p, position) {
    var self = this;
    var wl = this.wl;
    var on = !wl.saved[p.handle];
    wl.saved[p.handle] = on;
    wl.error = '';
    this.track(on ? 'brewer_quiz_hearted' : 'brewer_quiz_unhearted', { handle: p.handle, position: position });
    if (wl.token) {
      var body = on
        ? { token: wl.token, handle: p.handle, position: position, answers: this.answerSummary() }
        : { token: wl.token, handle: p.handle };
      this.wlPost(on ? '/wishlist/add' : '/wishlist/remove', body).then(function (res) {
        if (res.ok) return;
        // Say it failed. A save that quietly does nothing is the bug this replaced.
        wl.saved[p.handle] = !on;
        wl.error = 'That did not save. Please try again.';
        if (self.current === 'RESULTS') self.renderResultTab(false);
      });
    } else if (on && wl.state === 'none') {
      wl.ask = true;
    }
    this.renderResultTab(false);
  };

  // The strip above the price, once something is hearted and the sheet is shut.
  Quiz.prototype.wishlistStripHtml = function () {
    var wl = this.wl;
    if (!wl || wl.ask) return '';
    var any = this.result.shown.some(function (p) { return wl.saved[p.handle]; });
    if (!any && !wl.error) return '';
    var html = '<div class="bq-strip">';
    if (wl.error) {
      html += '<span>' + esc(wl.error) + '</span>';
    } else if (wl.state === 'done') {
      html += '<span>Your wishlist is saved.</span><a class="bq-textlink" href="' + esc(wl.url || '/pages/wishlist') + '">Open your wishlist</a>';
    } else {
      html += '<span>Saved until you leave this page.</span><button type="button" class="bq-textlink" data-bq-keep-open>Email me the link</button>';
    }
    return html + '</div>';
  };

  Quiz.prototype.wishlistSheetHtml = function (p) {
    var wl = this.wl;
    if (!wl || !wl.ask) return '';
    var html = '<div class="cq2-overlay" data-bq-keep-sheet>';
    html += '<div class="cq2-modal bq-keep" role="dialog" aria-modal="true" aria-labelledby="bq-keep-title-' + esc(this.root.id) + '">';
    html += '<button type="button" class="cq2-close" data-bq-keep-close aria-label="Close">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';
    if (wl.state === 'done') {
      html += '<h3 class="cq2-modal-title" id="bq-keep-title-' + esc(this.root.id) + '">Your wishlist is saved</h3>';
      html += '<p class="bq-sheet-text">We sent the link to your wishlist page to ' + esc(wl.email || 'your email') +
        '. Machines you save from now on are added to the same list.</p>';
      html += '<button type="button" class="cq2-btn cq2-btn--wide" data-bq-keep-close>Back to your results</button>';
      html += '<a class="bq-textlink bq-textlink--center" href="' + esc(wl.url || '/pages/wishlist') + '">Open your wishlist</a>';
    } else {
      html += '<h3 class="cq2-modal-title" id="bq-keep-title-' + esc(this.root.id) + '">Keep your wishlist</h3>';
      html += '<p class="bq-sheet-text">The ' + esc(p.title) + ' is on your wishlist until you leave this page. ' +
        'To keep the list, enter your email and we will send you a link to your wishlist page.</p>';
      html += '<form class="bq-keep-form" data-bq-keep-form novalidate>';
      html += '<label class="bq-label" for="bq-keep-email-' + esc(this.root.id) + '">Email address</label>';
      html += '<input class="bq-input" id="bq-keep-email-' + esc(this.root.id) + '" type="email" name="email" autocomplete="email" ' +
        'placeholder="you@example.com" value="' + esc(this.cfg.customerEmail || '') + '" required>';
      html += '<p class="bq-error" data-bq-keep-error hidden></p>';
      html += '<button type="submit" class="cq2-btn cq2-btn--wide">Email me the link</button>';
      html += '</form>';
      html += '<button type="button" class="bq-textlink bq-textlink--center" data-bq-keep-close>Not now</button>';
    }
    return html + '</div></div>';
  };

  Quiz.prototype.wireWishlist = function (p, position) {
    var self = this;
    var wl = this.wl;
    if (!wl) return;
    var heart = this.stage.querySelector('[data-bq-heart]');
    if (heart) heart.addEventListener('click', function () { self.heartClick(p, position); });
    var open = this.stage.querySelector('[data-bq-keep-open]');
    if (open) {
      open.addEventListener('click', function () {
        wl.ask = true;
        wl.state = 'none';
        self.renderResultTab(false);
      });
    }
    var sheet = this.stage.querySelector('[data-bq-keep-sheet]');
    if (!sheet) return;
    function close() {
      wl.ask = false;
      if (wl.state !== 'done') wl.state = 'dismissed';
      self.renderResultTab(false);
    }
    sheet.addEventListener('click', function (e) {
      if (e.target === sheet || e.target.closest('[data-bq-keep-close]')) close();
    });
    var input = sheet.querySelector('input[name=email]');
    if (input && !input.value) input.focus();
    var form = sheet.querySelector('[data-bq-keep-form]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (input.value || '').trim();
      var err = sheet.querySelector('[data-bq-keep-error]');
      var btn = form.querySelector('button[type=submit]');
      var shown = self.result.shown;
      var items = shown
        .map(function (s, i) { return { handle: s.handle, position: i + 1, title: s.title, answers: self.answerSummary() }; })
        .filter(function (it) { return wl.saved[it.handle]; });
      btn.disabled = true;
      btn.textContent = 'Sending…';
      var body = { email: email, items: items };
      var cid = shopifyCustomerId();
      if (cid) body.customerId = cid;
      self.wlPost('/wishlist', body).then(function (res) {
        if (!res.ok) {
          btn.disabled = false;
          btn.textContent = 'Email me the link';
          err.hidden = false;
          err.textContent = res.data.message || 'That did not save. Please try again.';
          return;
        }
        storeToken(res.data.token);
        wl.token = res.data.token;
        wl.url = res.data.url || '';
        wl.email = email;
        wl.state = 'done';
        self.track('brewer_quiz_saved', { count: items.length });
        self.renderResultTab(false);
      });
    });
  };

  // --------------------------------------------------------------- render ---
  //
  // Every screen is the coffee quiz's (sections/coffee-quiz-v2.liquid) with its
  // cq2-* classes and markup. Do not add classes or styles here: if a screen
  // needs to change, change the coffee quiz and copy it across.

  var CHEVRON_LEFT = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>';
  var CHEVRON_DOWN = '<svg class="cq2-recent-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';

  function sized(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function plain(html) {
    return String(html || '').replace(/<[^>]+>/g, '');
  }

  // The stage takes the coffee quiz's screen classes for whichever screen it is
  // showing. The back-and-progress bar shows on question screens only, as there.
  Quiz.prototype.screen = function (cls, html, chrome) {
    this.stage.className = 'cq2-screen ' + cls;
    this.stage.innerHTML = html;
    this.stage.scrollTop = 0;
    this.nav.hidden = !chrome;
  };

  // Fill the viewport below the site header, so every step fits on one screen
  // and the sections under the quiz never move as the shopper goes from step to
  // step. The coffee quiz's fit(), unchanged.
  Quiz.prototype.fit = function () {
    var top = this.root.getBoundingClientRect().top + window.scrollY;
    var below = window.innerHeight - top;
    this.root.style.height = (below >= 520 ? below : Math.max(520, window.innerHeight)) + 'px';
  };

  Quiz.prototype.render = function () {
    this.pendingResults = null;
    // Results render in place. Never scroll the page.
    if (this.current === 'COVER') return this.renderCover();
    if (this.current === 'RESULTS' || !this.steps[this.current]) {
      this.current = 'RESULTS';
      return this.showResults();
    }
    return this.renderStep(this.steps[this.current]);
  };

  // A cover card: an <a> when it goes somewhere, a <button> when it starts the
  // quiz in place. The coffee quiz's .cq2-path.
  function pathCard(href, img, title, sub, attr) {
    var open = href
      ? '<a class="cq2-path" href="' + esc(href) + '" ' + attr + '>'
      : '<button type="button" class="cq2-path" ' + attr + '>';
    return open +
      '<span class="cq2-path-img">' + (img ? '<img src="' + esc(img) + '" alt="" loading="eager">' : '') + '</span>' +
      '<span class="cq2-path-text"><span class="cq2-path-title">' + esc(title) + '</span>' +
      (sub ? '<span class="cq2-sub">' + esc(sub) + '</span>' : '') + '</span>' +
      (href ? '</a>' : '</button>');
  }

  // With quiz_url set the section is a teaser: the cover alone, its quiz card a
  // link to the quiz page. The coffee quiz's "Cover buttons open the quiz page".
  Quiz.prototype.renderTeaser = function () {
    var self = this;
    var html = '<h2 class="cq2-h1">' + esc(this.cfg.coverHeading || '') + '</h2><div class="cq2-paths">' +
      pathCard(this.cfg.quizUrl, this.cfg.coverImage, this.cfg.coverButton || 'Take the quiz',
        plain(this.cfg.coverBody), 'data-bq-teaser') + '</div>';
    this.screen('cq2-cover', html, false);
    this.stage.querySelector('[data-bq-teaser]').addEventListener('click', function () {
      self.track('brewer_quiz_teaser_clicked', { from: window.location.pathname });
    });
  };

  Quiz.prototype.renderCover = function () {
    var self = this;
    var c = this.cfg;
    var html = '<h2 class="cq2-h1">' + esc(c.coverHeading) + '</h2><div class="cq2-paths">';
    html += pathCard(null, c.coverImage, c.coverButton || 'Take the quiz', plain(c.coverBody), 'data-bq-start');
    if (c.coverSecondaryLabel && c.coverSecondaryAnchor) {
      html += pathCard(c.coverSecondaryAnchor, c.browseImage, c.coverSecondaryLabel, c.browseBody, 'data-bq-browse');
    }
    html += '</div>';
    this.screen('cq2-cover', html, false);
    this.stage.querySelector('[data-bq-start]').addEventListener('click', function () {
      self.start();
    });
  };

  Quiz.prototype.start = function () {
    this.current = 'Q1';
    this.track('brewer_quiz_started', {});
    this.render();
  };

  // "Stillwater recommends" is a flag on the answer, the way the coffee quiz
  // flags Barista's choice on a tasting note: the same pill in the same brown.
  function isFlagged(option, level) {
    return !!(option.recommended ||
      (option.recommendedFor && level && option.recommendedFor.indexOf(level) !== -1));
  }

  Quiz.prototype.renderStep = function (step) {
    if (!step) {
      this.current = 'RESULTS';
      return this.showResults();
    }

    var self = this;
    var level = this.answeredLevel();
    var photos = [];
    var rest = [];
    step.options.forEach(function (option, i) { (option.img ? photos : rest).push(i); });

    var html = '<h2 class="cq2-h2">' + esc(step.title) + '</h2>';
    if (photos.length) {
      // Photo answers are the coffee quiz's brew-method cards. An answer with no
      // photo ("Not sure yet", "No preference") is its "Help me choose" button.
      html += '<div class="cq2-brew-grid' + (photos.length === 2 ? ' cq2-brew-grid--2' : '') + '">';
      photos.forEach(function (i) {
        var option = step.options[i];
        var width = option.img.length > 1 ? 500 : 700;
        html += '<button type="button" class="cq2-opt cq2-brew" data-index="' + i + '">';
        html += '<span class="cq2-brew-img">' + option.img.map(function (src) {
          return '<img src="' + esc(sized(src, width)) + '" alt="" loading="lazy">';
        }).join('') + '</span>';
        // The "Stillwater recommends" flag rides the card's top edge here
        // rather than taking a line of its own: on a phone that line pushed
        // the description out of a card sized to the screen.
        if (isFlagged(option, level)) {
          html += '<span class="cq2-flag bq-edge-flag" style="background:#8A5F38">Stillwater recommends</span>';
        }
        html += '<span class="cq2-brew-text"><span class="cq2-brew-label">' + esc(option.label) + '</span>' +
          (option.sub ? '<span class="cq2-sub">' + esc(option.sub) + '</span>' : '') + '</span>';
        html += '</button>';
      });
      html += '</div>';
      rest.forEach(function (i) {
        html += '<div class="cq2-help"><button type="button" class="cq2-btn cq2-btn--outline" data-index="' + i + '">' +
          esc(step.options[i].label) + '</button></div>';
      });
    } else {
      // Text answers are the coffee quiz's milk-question cards.
      html += '<div class="cq2-milk-grid' + (step.options.length === 4 ? ' cq2-milk-grid--4' : '') + '">';
      step.options.forEach(function (option, i) {
        // "None. I have never pulled a shot." shows "None" at answer size and
        // the rest on a second line in the smaller sub style. See splitAnswer().
        // An explicit `sub` stays hidden here, as it was before the split.
        var text = esc(option.label);
        if (!option.sub) {
          var parts = splitAnswer(option);
          text = '<span class="cq2-milk-lead">' + esc(parts.lead) + '</span>' +
            (parts.note ? '<span class="cq2-sub">' + esc(parts.note) + '</span>' : '');
        }
        html += '<button type="button" class="cq2-opt cq2-milk" data-index="' + i + '">' + text + '</button>';
      });
      html += '</div>';
    }

    this.screen('cq2-q', html, true);

    Array.prototype.forEach.call(this.stage.querySelectorAll('[data-index]'), function (btn) {
      btn.addEventListener('click', function () {
        btn.classList.add('is-picked');
        self.choose(step.options[parseInt(btn.dataset.index, 10)]);
      });
    });
    this.setProgress();
  };

  Quiz.prototype.setProgress = function () {
    // Roughly eight questions on either arm; enough to feel honest without
    // pretending we know the exact remaining depth.
    var total = 8;
    var done = Math.min(this.history.length + 1, total);
    this.progressFill.style.width = Math.round((done / (total + 1)) * 100) + '%';
  };

  // ---------------------------------------------------------------- engine ---

  function collect(history) {
    var filters = { family: null, milk: null, grinder_builtin: null, simul_steam: null, capacity: null, maxPrice: null, stretchPrice: null, minPrice: null };
    var weights = {}; // axis -> { w, tSum, tWeight }
    // How to name a filter if it has to be set aside, from the answer that set
    // it (an option's `relax`). RELAX holds the fallback wording.
    var relaxLabels = {};

    function addWeight(axis, w, t) {
      if (!weights[axis]) weights[axis] = { w: 0, tSum: 0, tWeight: 0, target: null };
      var entry = weights[axis];
      entry.w += w;
      if (typeof t === 'number') {
        entry.tSum += t * w;
        entry.tWeight += w;
      } else if (t != null) {
        entry.target = t;
      }
    }

    history.forEach(function (record) {
      var option = record.option;

      if (option.filter) {
        Object.keys(option.filter).forEach(function (key) {
          var value = option.filter[key];
          if (option.relax) relaxLabels[key] = option.relax;
          if (key === 'family' || key === 'capacity') {
            // Intersect: later answers can only narrow, never widen.
            filters[key] = filters[key] ? filters[key].filter(function (v) { return value.indexOf(v) !== -1; }) : value.slice();
          } else if (key === 'maxPrice' || key === 'stretchPrice') {
            filters[key] = filters[key] == null ? value : Math.min(filters[key], value);
          } else {
            filters[key] = value;
          }
        });
      }

      if (option.weight) {
        option.weight.forEach(function (weight) { addWeight(weight.axis, weight.w, weight.t); });
      }

      // Q7 doubles its axis. Add a base weight first so an axis nothing else
      // touched still lands at the top of the ranking.
      if (option.primary) {
        option.primary.forEach(function (axis) {
          addWeight(axis, 2, axis === 'price' ? 'low' : 5);
          weights[axis].w *= 2;
        });
      }
    });

    // Resolve blended numeric targets.
    Object.keys(weights).forEach(function (axis) {
      var entry = weights[axis];
      if (entry.tWeight > 0) entry.target = entry.tSum / entry.tWeight;
    });

    return { filters: filters, weights: weights, relaxLabels: relaxLabels };
  }

  // `ceiling` and `floor` override the budget filter so the same rules can be
  // reused for the over-budget stretch pool and the under-budget pool.
  function survives(product, filters, ceiling, floor) {
    var limit = ceiling === undefined ? filters.maxPrice : ceiling;
    var lower = floor === undefined ? filters.minPrice : floor;
    if (lower != null && product.price < lower) return false;
    if (filters.family && filters.family.indexOf(product.family) === -1) return false;
    if (filters.capacity && filters.capacity.indexOf(product.capacity) === -1) return false;
    if (limit != null && product.price > limit) return false;
    if (filters.milk && product.milk !== filters.milk) return false;
    if (filters.grinder_builtin === 1 && product.grinder_builtin !== 1) return false;
    if (filters.grinder_builtin === 0 && product.grinder_builtin === 1) return false;
    if (filters.simul_steam === 1 && product.simul_steam !== 1) return false;
    return true;
  }

  function axisValue(product, axis) {
    if (axis === 'capacity') return CAPACITY_ORDER[product.capacity] || 1;
    return product[axis];
  }

  function similarity(product, axis, target, priceRange) {
    var spec = AXES[axis];
    if (!spec) return 0;

    if (spec.type === 'price') {
      // "Best value" = cheapest of what actually fits.
      if (priceRange.span === 0) return 1;
      return (priceRange.max - product.price) / priceRange.span;
    }
    if (spec.type === 'bool') {
      return product[axis] === 1 ? 1 : 0;
    }
    if (spec.type === 'boiler') {
      // How many drinks at a time is really a question about the boiler. A
      // single boiler brews or steams and makes you wait between; a heat
      // exchanger does both at once; two boilers do both at once and recover
      // fastest. Four rows have no sourced boiler, and those score neutral so a
      // gap in our data neither flatters nor punishes the machine.
      var rank = BOILER_ORDER[product.boiler];
      if (!rank) return 0.5;
      return 1 - Math.abs(rank - target) / 3;
    }
    if (spec.type === 'ord') {
      var t = typeof target === 'number' ? target : (CAPACITY_ORDER[target] || 1);
      return 1 - Math.abs(axisValue(product, axis) - t) / 3;
    }
    return 1 - Math.abs(product[axis] - target) / NUMERIC_RANGE;
  }

  // When no machine clears every must-have, the quiz still recommends (Dean,
  // 2026-10-10: a dead end is not acceptable). It sets aside the fewest, least
  // important must-haves that leave something standing, and says which.
  //
  // Cost of setting each one aside, cheapest first. The budget comes first
  // because it is usually the cause, and stretching it one band is the mildest
  // change there is; dropping it outright costs more than any single feature.
  // The machine type (espresso or filter) is the last thing to give up.
  var RELAX = [
    { key: 'simul_steam', cost: 2, label: 'brewing and steaming at the same time' },
    { key: 'grinder_builtin', cost: 3, label: 'a built-in grinder' },
    { key: 'capacity', cost: 4, label: 'the batch size' },
    { key: 'milk', cost: 5, label: 'how it handles milk' },
    { key: 'family', cost: 9, label: 'the type of machine' }
  ];
  var BUDGET_STRETCH_COST = 1;
  var BUDGET_DROP_COST = 6;

  function relax(products, filters, names) {
    names = names || {};
    var keys = RELAX.filter(function (r) { return filters[r.key] != null; });
    var budgets = [{ cost: 0, maxPrice: filters.maxPrice, minPrice: filters.minPrice }];
    if (filters.maxPrice != null || filters.minPrice != null) {
      if (filters.maxPrice != null && filters.stretchPrice != null && filters.stretchPrice > filters.maxPrice) {
        budgets.push({ cost: BUDGET_STRETCH_COST, maxPrice: filters.stretchPrice, minPrice: filters.minPrice, label: 'your budget' });
      }
      // A cheaper machine than the band is as mild a change as a band up.
      if (filters.minPrice != null) {
        budgets.push({ cost: BUDGET_STRETCH_COST, maxPrice: filters.maxPrice, minPrice: null, label: 'your budget' });
      }
      budgets.push({ cost: BUDGET_DROP_COST, maxPrice: null, minPrice: null, label: 'your budget' });
    }
    var best = null;
    for (var mask = 0; mask < (1 << keys.length); mask++) {
      budgets.forEach(function (budget) {
        if (!mask && !budget.cost) return;
        var f = Object.assign({}, filters, { maxPrice: budget.maxPrice, minPrice: budget.minPrice });
        var cost = budget.cost;
        var labels = budget.label ? [budget.label] : [];
        keys.forEach(function (r, i) {
          if (mask & (1 << i)) { f[r.key] = null; cost += r.cost; labels.push(names[r.key] || r.label); }
        });
        var count = products.filter(function (p) { return survives(p, f); }).length;
        if (!count) return;
        if (!best || cost < best.cost || (cost === best.cost && count > best.count)) {
          best = { filters: f, cost: cost, count: count, labels: labels, budgetLoosened: budget.cost > 0 };
        }
      });
    }
    return best;
  }

  function score(products, history) {
    var collected = collect(history);
    var asked = collected.filters;
    var relaxed = null;
    var survivors = products.filter(function (p) { return survives(p, asked); });
    if (!survivors.length) {
      relaxed = relax(products, asked, collected.relaxLabels);
      if (!relaxed) return { survivors: [], stretch: [], filters: asked, weights: collected.weights };
      collected.filters = relaxed.filters;
      survivors = products.filter(function (p) { return survives(p, relaxed.filters); });
    }

    var prices = survivors.map(function (p) { return p.price; });
    var priceRange = { min: Math.min.apply(null, prices), max: Math.max.apply(null, prices) };
    priceRange.span = priceRange.max - priceRange.min;

    var weights = collected.weights;
    var axes = Object.keys(weights);
    var totalWeight = axes.reduce(function (sum, axis) { return sum + weights[axis].w; }, 0);

    function fitFor(product) {
      var sum = 0;
      axes.forEach(function (axis) {
        sum += weights[axis].w * clamp01(similarity(product, axis, weights[axis].target, priceRange));
      });
      return totalWeight ? sum / totalWeight : 0.5;
    }

    var scored = survivors.map(function (product) {
      var entry = Object.assign({}, product, { fit: fitFor(product) });
      // With the budget set aside, a machine over it says so, as a stretch
      // pick does.
      if (relaxed && asked.maxPrice != null && product.price > asked.maxPrice) {
        entry.overBudget = Math.round(product.price - asked.maxPrice);
      }
      if (relaxed && asked.minPrice != null && product.price < asked.minPrice) {
        entry.underBudget = Math.round(asked.minPrice - product.price);
      }
      return entry;
    });

    // Ties are common — plenty of machines share a spec sheet. Break them on
    // price so the cheaper of two equally-good machines always wins, and so
    // the ranking is deterministic rather than an accident of file order.
    // Whether we sell a machine counts only when fit and price are both equal.
    function byFitThenPrice(a, b) {
      if (Math.abs(b.fit - a.fit) > 1e-9) return b.fit - a.fit;
      if (a.price !== b.price) return a.price - b.price;
      return (a.sold === 0 ? 1 : 0) - (b.sold === 0 ? 1 : 0);
    }
    scored.sort(byFitThenPrice);

    // Machines that clear every filter except the budget, up to the next band.
    // They can only ever fill an ALTERNATE slot — the best fit stays inside the
    // stated budget.
    var stretch = [];
    var f = collected.filters;
    if (f.maxPrice != null && f.stretchPrice != null && f.stretchPrice > f.maxPrice) {
      stretch = products
        .filter(function (p) {
          return p.price > f.maxPrice && survives(p, f, f.stretchPrice);
        })
        .map(function (product) {
          return Object.assign({}, product, { fit: fitFor(product), overBudget: Math.round(product.price - f.maxPrice) });
        })
        .sort(byFitThenPrice);
    }

    // Machines that clear every filter except the budget's floor. At most one,
    // and only in the third slot (Dean, 2026-10-21): the best match and the
    // second pick stay inside the band the buyer chose.
    var below = [];
    if (f.minPrice != null) {
      below = products
        .filter(function (p) {
          return p.price < f.minPrice && survives(p, f, undefined, null);
        })
        .map(function (product) {
          return Object.assign({}, product, { fit: fitFor(product), underBudget: Math.round(f.minPrice - product.price) });
        })
        .sort(byFitThenPrice);
    }

    return {
      survivors: scored,
      stretch: stretch,
      below: below,
      filters: asked,
      weights: weights,
      priceRange: priceRange,
      // What had to be set aside to recommend anything; null when nothing was.
      relaxed: relaxed ? relaxed.labels : null
    };
  }

  // Step 3: pick up to two alternates that still score well but differ most on
  // an axis the buyer weighted. That difference is what makes them worth showing.
  // `stretch` holds machines one band above budget; at most one may be shown, so
  // the buyer always keeps an in-budget comparison.
  // `below` holds machines under the budget's floor; at most one may be shown,
  // always last.
  function pickSpread(scored, weights, stretch, below) {
    if (!scored.length) return [];
    if (scored.length === 1 && !(stretch && stretch.length) && !(below && below.length)) return scored.slice(0, 1);

    var best = scored[0];
    // Wide enough that a cheaper, plainer machine still gets to compete for a
    // card — that "best value" contrast is usually the most useful alternate.
    var shortlist = scored.slice(1, 8).filter(function (p) { return p.fit >= best.fit * 0.7; });
    if (!shortlist.length) shortlist = scored.slice(1, 3);

    // The over-budget candidate only earns its slot if it genuinely beats the
    // best fit on something — otherwise it is just a more expensive machine.
    if (stretch && stretch.length) {
      var contender = stretch.filter(function (p) { return p.fit > best.fit; })[0];
      if (contender) shortlist = shortlist.concat([contender]);
    }

    var axes = Object.keys(weights);
    function distance(product, others) {
      var total = 0;
      axes.forEach(function (axis) {
        if (AXES[axis].type === 'price') return;
        others.forEach(function (other) {
          var range = AXES[axis].type === 'ord' ? 3 : NUMERIC_RANGE;
          total += weights[axis].w * Math.abs(axisValue(product, axis) - axisValue(other, axis)) / range;
        });
      });
      others.forEach(function (other) {
        // A different family is a real, legible difference in its own right.
        if (product.family !== other.family) total += 1;
        // Two trims of the same range read as one recommendation, not two.
        if (brandOf(product) !== brandOf(other)) total += 1.5;
      });
      return total / others.length;
    }

    // The cheaper candidate is held to the same bar as the shortlist, and may
    // only compete for the last slot.
    var cheaper = (below || []).filter(function (p) { return p.fit >= best.fit * 0.7; })[0];

    var picked = [best];
    for (var n = 0; n < 2; n++) {
      // The second slot, or the first alternate when nothing else in the band
      // is left to fill it, since it is then the last card shown.
      var lastCard = n === 1 || !shortlist.length;
      var pool = lastCard && cheaper ? shortlist.concat([cheaper]) : shortlist;
      if (!pool.length) break;
      pool.sort(function (a, b) { return distance(b, picked) - distance(a, picked); });
      var next = pool[0];
      picked.push(next);
      if (next === cheaper) break;
      shortlist = shortlist.filter(function (p) { return p !== next; });
    }

    // Best fit leads; the alternates read better ordered by how well they fit,
    // except that a below-budget pick always sits last.
    var alternates = picked.slice(1).sort(function (a, b) {
      if (!!a.underBudget !== !!b.underBudget) return a.underBudget ? 1 : -1;
      return b.fit - a.fit;
    });
    return [best].concat(alternates);
  }

  // The hat for leading on `tech`: what the machine has, rather than a word
  // like "Smartest". Read off the score, which carries the review rulings (a
  // small LED readout is 2 even where a source called it a screen); the
  // research's `display` fact only says which of the two kinds a 5 is.
  function techLabel(product) {
    if (product.tech >= 5) return product.display === 'app' ? 'App control' : 'Touchscreen';
    if (product.tech >= 3) return 'Has a screen';
    return 'Temperature display';
  }

  function brandOf(product) {
    return String(product.title).split(' ')[0].toLowerCase();
  }

  // Each alternate's hat is the axis it leads the shown set on. Assigned
  // greedily by margin so no two cards wear the same hat. The best fit is
  // already labelled "your best fit" and takes no hat — but it still competes,
  // so an alternate never claims a hat the best fit actually leads on.
  function assignHats(shown) {
    var claims = [];
    shown.forEach(function (product, index) {
      HATS.forEach(function (hat) {
        var mine = hat.axis === 'price' ? product.price : axisValue(product, hat.axis);
        var margin = null;
        shown.forEach(function (other, j) {
          if (j === index) return;
          var theirs = hat.axis === 'price' ? other.price : axisValue(other, hat.axis);
          var gap = hat.dir === 'low' ? theirs - mine : mine - theirs;
          if (hat.axis === 'price') gap = gap / Math.max(1, other.price); // relative
          margin = margin === null ? gap : Math.min(margin, gap);
        });
        if (margin !== null && margin > 0) {
          claims.push({ index: index, label: hat.label || techLabel(product), margin: margin });
        }
      });
    });

    claims.sort(function (a, b) { return b.margin - a.margin; });
    var hats = {};
    var used = {};
    var taken = {};
    claims.forEach(function (claim) {
      if (taken[claim.index] || used[claim.label]) return;
      taken[claim.index] = true;
      used[claim.label] = true;
      if (claim.index !== 0) hats[claim.index] = claim.label;
    });
    return hats;
  }

  // --------------------------------------------------------------- copy ---

  function whyLine(axis, product) {
    var v = product[axis];
    switch (axis) {
      case 'automation':
        if (v >= 5) return 'Grinds, brews and handles milk at one touch.';
        if (v === 4) return 'Push a button and walk away.';
        if (v <= 2) return 'Every step is yours.';
        return 'Simple to run, no technique required.';
      case 'control':
        if (v >= 5) return 'Full shot control, down to flow profiling.';
        if (v === 4) return 'Temperature and shot timing are yours to set.';
        if (v <= 2) return 'One button, and it is done.';
        return 'Enough adjustment to dial a coffee in.';
      case 'warmup':
        if (v >= 5) return 'Ready in seconds.';
        if (v <= 2) return 'Wants a warm-up, then holds temperature all morning.';
        return 'Warms up in a couple of minutes.';
      case 'skill':
        if (v <= 1) return 'Forgiving of small mistakes.';
        if (v >= 4) return 'Rewards practice. There is a learning curve.';
        return 'A little practice and you are there.';
      case 'maint_ease':
        if (v >= 5) return 'Rinse and go.';
        if (v <= 2) return 'Expect regular backflushing and descaling.';
        return 'Straightforward to keep clean.';
      case 'durability':
        if (v >= 5) return 'Built to last, with parts you can replace.';
        if (v <= 2) return 'Light-duty build.';
        return 'Solid build for daily use.';
      case 'aesthetics':
        if (v >= 5) return 'The kind of machine you leave on the counter.';
        return null;
      case 'footprint':
        if (v >= 4) return 'Small enough for a tight counter.';
        if (v <= 2) return 'Takes real counter space.';
        return null;
      case 'tech':
        if (v >= 5) return product.display === 'app'
          ? 'You set it up from a phone app.'
          : 'You set it up on a touchscreen.';
        if (v <= 1) return 'You run it with buttons and dials.';
        return null;
      case 'capacity':
        return CAPACITY_WORDS[product.capacity] === 'One cup'
          ? 'Brews one cup at a time.'
          : 'Brews a ' + CAPACITY_WORDS[product.capacity].toLowerCase() + ' at a time.';
      case 'plumbable':
        return product.plumbable ? 'Can be plumbed into a water line.' : null;
      default:
        return null;
    }
  }

  // The other half of an honest recommendation: what the buyer is signing up
  // for. Fires when a product sits at the wrong end of an axis, whether or not
  // they weighted it — the caveats people get burned by are usually the ones
  // they never thought to ask about.
  function downsideLine(axis, product) {
    var v = product[axis];
    switch (axis) {
      case 'automation':
        if (v <= 2) return 'Nothing is automated. Every cup is hands-on, start to finish.';
        return null;
      case 'control':
        if (v <= 1) return 'It brews one way, and that is the cup you get.';
        return null;
      case 'warmup':
        if (v <= 2) return 'Needs a long warm-up before the first shot, so mornings take planning.';
        return null;
      case 'skill':
        if (v >= 4) return 'There is a real learning curve before it makes good coffee.';
        if (v === 3) return 'Takes some practice to get consistent.';
        return null;
      case 'maint_ease':
        if (v <= 2) return 'Backflushing and descaling on a schedule.';
        if (v === 3) return 'Regular cleaning, not a rinse-and-go machine.';
        return null;
      case 'durability':
        if (v <= 2) return 'Lighter build than most of the machines here.';
        return null;
      case 'aesthetics':
        if (v <= 2) return 'Plain to look at.';
        return null;
      case 'footprint':
        if (v <= 2) return 'Takes real counter space, and it is heavy.';
        return null;
      case 'tech':
        if (v <= 1) return 'You run it with buttons and dials.';
        return null;
      case 'capacity':
        if (product.capacity === 'single') return 'One cup at a time.';
        return null;
      default:
        return null;
    }
  }

  // Where a product lands on the wrong side of something the buyer explicitly
  // asked for. `below` is true when the product scores under their target.
  function shortfallLine(axis, below) {
    switch (axis) {
      case 'durability': return below ? 'Built lighter than the durability you asked for.' : null;
      case 'control': return below ? 'Less control than you said you wanted.' : 'More to fiddle with than you asked for.';
      case 'automation': return below ? 'More hands-on than you asked for.' : 'More automated than you wanted, so less of the process is yours.';
      case 'warmup': return below ? 'Slower to be ready than you wanted.' : null;
      case 'skill': return below ? null : 'Asks more technique than you wanted to learn.';
      case 'maint_ease': return below ? 'More upkeep than you asked for.' : null;
      case 'aesthetics': return below ? 'Plainer than you wanted it to look.' : null;
      case 'footprint': return below ? 'Bigger on the counter than you wanted.' : null;
      case 'capacity': return below ? 'Brews smaller batches than you wanted.' : 'Bigger batches than you need.';
      default: return null;
    }
  }

  // Up to two caveats, worst first. Weighted axes rank higher because those are
  // the ones the buyer told us they care about.
  function downsides(product, weights, shown) {
    var found = [];

    Object.keys(AXES).forEach(function (axis) {
      if (axis === 'price' || axis === 'plumbable') return;
      var w = weights[axis] ? weights[axis].w : 0;

      // Missing something they asked for outranks a generic caveat.
      if (w > 0) {
        var target = weights[axis].target;
        var sim = clamp01(similarity(product, axis, target, { span: 0 }));
        if (sim < 0.65) {
          var t = typeof target === 'number' ? target : (CAPACITY_ORDER[target] || 0);
          var line = shortfallLine(axis, axisValue(product, axis) < t);
          if (line) {
            found.push({ line: line, rank: w + 20 });
            return;
          }
        }
      }

      var generic = downsideLine(axis, product);
      if (generic) found.push({ line: generic, rank: w + 1 });
    });

    // Buying a grinder afterwards is the caveat people most often miss.
    if (product.needs_grinder) {
      found.push({ line: 'Budget for a separate grinder on top of this.', rank: 100 });
    }

    if (product.overBudget) {
      // Blowing the stated budget outranks every other caveat.
      found.push({ line: 'Costs ' + money(product.overBudget) + ' more than the budget you set.', rank: 200 });
    } else if (shown && shown.length > 1) {
      // Only meaningful against the other cards, so it lives here rather than
      // in the per-axis map. Redundant once we have said it is over budget.
      var dearest = shown.reduce(function (a, b) { return b.price > a.price ? b : a; });
      if (dearest.handle === product.handle) {
        found.push({ line: 'The most expensive of the three shown.', rank: 50 });
      }
    }

    found.sort(function (a, b) { return b.rank - a.rank; });
    return found.slice(0, 2).map(function (f) { return f.line; });
  }

  // Up to `limit` reasons this product suits what they answered, strongest
  // weighted axis first. An axis only earns a line if the product is actually
  // GOOD on it — otherwise "solid build" turns up as a selling point on the
  // flimsiest of the three.
  function upsides(product, weights, limit) {
    var axes = Object.keys(weights).sort(function (a, b) { return weights[b].w - weights[a].w; });
    var lines = [];
    axes.forEach(function (axis) {
      if (lines.length >= limit) return;
      if (clamp01(similarity(product, axis, weights[axis].target, { span: 0 })) < 0.75) return;
      var line = whyLine(axis, product);
      if (line && lines.indexOf(line) === -1) lines.push(line);
    });
    // Fall back to the product's own standout traits if they weighted almost
    // nothing, so a card is never left with an empty list.
    if (!lines.length) {
      ['automation', 'control', 'durability', 'capacity'].forEach(function (axis) {
        if (lines.length >= limit) return;
        var line = whyLine(axis, product);
        if (line && lines.indexOf(line) === -1) lines.push(line);
      });
    }
    return lines;
  }

  function prosConsHtml(product, weights, shown, likeHeading) {
    var pros = upsides(product, weights, 3);
    var cons = downsides(product, weights, shown);
    var html = '';

    if (pros.length) {
      html += '<p class="caption-with-letter-spacing brewer-quiz__eyebrow">' + esc(likeHeading) + '</p><ul class="brewer-quiz__list">';
      pros.forEach(function (p) { html += '<li>' + esc(p) + '</li>'; });
      html += '</ul>';
    }
    if (cons.length) {
      html += '<p class="caption-with-letter-spacing brewer-quiz__eyebrow">What you might not like</p><ul class="brewer-quiz__list brewer-quiz__list--con">';
      cons.forEach(function (c) { html += '<li>' + esc(c) + '</li>'; });
      html += '</ul>';
    }
    return html;
  }

  // Clauses, not noun fragments. These get stitched into a sentence, so each
  // one has to survive being read aloud after "It".
  var GAIN_WORDS = {
    control: 'gives you more control',
    automation: 'asks less of you',
    warmup: 'is ready sooner',
    durability: 'is built tougher',
    aesthetics: 'looks better on a counter',
    footprint: 'takes up less room',
    maint_ease: 'is easier to clean',
    capacity: 'brews more at once'
  };
  var LOSS_WORDS = {
    control: 'gives you less control',
    automation: 'asks more of you',
    warmup: 'takes longer to warm up',
    durability: 'is built lighter',
    aesthetics: 'is plainer to look at',
    footprint: 'takes up more room',
    maint_ease: 'needs more cleaning',
    capacity: 'brews less at once'
  };

  // One plain sentence about how the alternate differs from the best fit.
  //
  // The old version pasted noun phrases together and wrapped them in a stock
  // opener, which produced things like "Close on the whole, with $31 more and
  // smaller batches." That is not a sentence and nobody talks that way. Money
  // now leads as its own clause, and every other difference is a verb phrase
  // that reads after "It".
  function tradeoffLine(alternate, best, weights) {
    var gains = [];
    var losses = [];

    Object.keys(weights).forEach(function (axis) {
      if (!GAIN_WORDS[axis]) return;
      var delta = axisValue(alternate, axis) - axisValue(best, axis);
      if (delta >= 1) gains.push({ word: GAIN_WORDS[axis], score: delta * weights[axis].w });
      if (delta <= -1) losses.push({ word: LOSS_WORDS[axis], score: -delta * weights[axis].w });
    });

    gains.sort(function (a, b) { return b.score - a.score; });
    losses.sort(function (a, b) { return b.score - a.score; });

    var gain = gains.length ? gains[0].word : null;
    var loss = losses.length ? losses[0].word : null;

    // Price is the difference everyone reads first, so it opens the sentence
    // in plain money terms rather than being filed under gains or losses.
    var priceDelta = Math.round(best.price - alternate.price);
    var cheaper = priceDelta >= 25;
    var dearer = priceDelta <= -25;

    if (cheaper) {
      if (loss) return 'Saves you ' + money(priceDelta) + ', but it ' + loss + '.';
      if (gain) return 'Saves you ' + money(priceDelta) + ' and ' + gain + '.';
      return 'Saves you ' + money(priceDelta) + ' for much the same machine.';
    }
    if (dearer) {
      if (gain) return 'Costs ' + money(-priceDelta) + ' more, and ' + gain + '.';
      if (loss) return 'Costs ' + money(-priceDelta) + ' more and ' + loss + '.';
      return 'Costs ' + money(-priceDelta) + ' more for much the same machine.';
    }
    if (gain && loss) return 'About the same money. It ' + gain + ', but ' + loss + '.';
    if (gain) return 'About the same money, and it ' + gain + '.';
    if (loss) return 'About the same money, but it ' + loss + '.';
    return 'Much the same machine for much the same money. Pick on looks.';
  }

  // -------------------------------------------------------------- results ---

  // The results screen: the coffee quiz's recommendation screen (photo, Your
  // match seal, Back and Retake, the name, price and Continue pinned at the
  // bottom), with tabs for the three machines that fit. Designed in Claude
  // Design, 2026-10-07 (artifact 5CEWazoGFR56JcKe9bQTyM, direction A): each tab
  // turns the screen into that machine's case, with why it fits, what you give
  // up and, for the other two, how it differs from your match.
  Quiz.prototype.renderResults = function () {
    var region = document.documentElement.getAttribute('data-gear-region') || 'ca';
    var result = score(buyableIn(this.products, region), this.history);

    // score() loosens the must-haves until something fits, so this is reached
    // only when no machine loaded at all. Same message as a failed load.
    if (!result.survivors.length) {
      this.screen('cq2-q',
        '<h2 class="cq2-h2">We could not load the machine data</h2>' +
        '<p class="cq2-sub"><a href="#ProductGridContainer">Browse the machines</a> in the meantime.</p>', true);
      this.track('brewer_quiz_no_results', {});
      return;
    }

    var shown = pickSpread(result.survivors, result.weights, result.stretch, result.below);
    this.result = { weights: result.weights, shown: shown, hats: assignHats(shown) };
    this.sel = 0;
    this.wishlistInit();
    this.renderResultTab(true);
    if (result.relaxed) this.showRelaxedNote(result.relaxed);

    this.track('brewer_quiz_completed', {
      best_fit: shown[0].handle,
      shown: shown.map(function (p) { return p.handle; }).join(','),
      survivors: result.survivors.length,
      relaxed: result.relaxed ? result.relaxed.join('|') : ''
    });

    klaviyoPush(['track', 'Brewer Quiz Completed', {
      answers: this.answerSummary(),
      best_fit: shown[0].handle,
      best_fit_price: shown[0].price,
      shown: shown.map(function (p) { return p.handle; }),
      survivor_count: result.survivors.length,
      budget_ceiling: result.filters.maxPrice,
      relaxed: result.relaxed || []
    }]);
  };

  // Shown once over the results when the must-haves had to be loosened: what
  // the buyer asked for has no exact match, so say so, and say what was set
  // aside, before they read the machines.
  Quiz.prototype.showRelaxedNote = function (labels) {
    var self = this;
    var id = 'bq-relaxed-title-' + this.root.id;
    var wrap = document.createElement('div');
    wrap.className = 'cq2-overlay';
    wrap.innerHTML = '<div class="cq2-modal bq-keep" role="dialog" aria-modal="true" aria-labelledby="' + esc(id) + '">' +
      '<h3 class="cq2-modal-title" id="' + esc(id) + '">You don\'t have any exact matches</h3>' +
      '<p class="bq-sheet-text">These are the closest machines if we set aside' + esc(joinList(labels)) + '.</p>' +
      '<button type="button" class="cq2-btn cq2-btn--wide" data-bq-relaxed-close>See the closest matches</button></div>';
    this.stage.appendChild(wrap);
    wrap.addEventListener('click', function (e) {
      if (e.target === wrap || e.target.closest('[data-bq-relaxed-close]')) wrap.remove();
    });
    wrap.querySelector('[data-bq-relaxed-close]').focus();
    this.track('brewer_quiz_relaxed', { relaxed: labels.join('|') });
  };

  // "a", "a and b", "a, b and c".
  function joinList(items) {
    if (items.length < 2) return items.join('');
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  var HEART_PATH = 'M12 21s-7.5-4.7-9.4-9A5.2 5.2 0 0 1 12 6.6 5.2 5.2 0 0 1 21.4 12c-1.9 4.3-9.4 9-9.4 9z';
  var HEART_SMALL = '<svg class="bq-tab-saved" width="12" height="12" viewBox="0 0 24 24" role="img" aria-label="Saved"><path d="' + HEART_PATH + '"/></svg>';

  function tabsHtml(shown, sel, cls, saved) {
    var html = '<div class="bq-tabs ' + cls + '" role="tablist" aria-label="The machines that fit">';
    shown.forEach(function (p, i) {
      var on = i === sel;
      html += '<button type="button" class="bq-tab" role="tab" aria-selected="' + (on ? 'true' : 'false') + '" data-bq-tab="' + i + '">' +
        '<img src="' + esc(sized(p.image, 160)) + '" alt="">' +
        '<span class="bq-tab-text"><span class="bq-tab-name">' + esc(p.title) + '</span>' +
        '<span class="bq-tab-price">' + esc(money(p.price)) +
        (saved && saved[p.handle] ? HEART_SMALL : '') + '</span></span></button>';
    });
    return html + '</div>';
  }

  function listHtml(heading, lines, cls) {
    if (!lines.length) return '';
    var html = '<div class="bq-list ' + cls + '"><h3 class="bq-list-head">' + esc(heading) + '</h3><ul>';
    lines.forEach(function (line) { html += '<li>' + esc(line) + '</li>'; });
    return html + '</ul></div>';
  }

  Quiz.prototype.renderResultTab = function (animate) {
    var self = this;
    var r = this.result;
    var shown = r.shown;
    var sel = this.sel;
    var best = shown[0];
    var p = shown[sel];
    var isMatch = sel === 0;
    var hat = sel > 0 ? r.hats[sel] : null;

    var saved = this.wl ? this.wl.saved : null;
    var html = tabsHtml(shown, sel, 'bq-tabs--phone', saved);
    html += '<div class="cq2-rec-img">';
    if (p.image) html += '<img class="cq2-rec-photo" src="' + esc(sized(p.image, 900)) + '" alt="' + esc(p.title) + '">';
    html += '<button type="button" class="cq2-back cq2-back--float" data-bq-rec-back aria-label="Back">' + CHEVRON_LEFT + '</button>';
    html += '<button type="button" class="cq2-retake" data-bq-retake>Retake the quiz</button>';
    if (isMatch) {
      html += '<div class="cq2-seal" aria-label="Your match"><span class="cq2-seal-burst"></span>' +
        '<span class="cq2-seal-disc"><span class="cq2-seal-text">Your<br>match</span></span></div>';
    }
    html += '</div>';

    html += '<div class="cq2-rec-body"><div class="cq2-rec-scroll"><div class="cq2-rec-inner">';
    html += tabsHtml(shown, sel, 'bq-tabs--desk', saved);
    html += '<div class="cq2-rec-head">';
    html += '<h2 class="cq2-rec-title' + (isMatch ? ' bq-title--seal' : '') + '">' + esc(p.title) + '</h2>';
    // The machine-type line ("Semi-automatic, dual boiler") was removed for
    // height on a phone (Dean, 2026-10-10). The flags stay, and the row only
    // renders when there is one.
    if (hat || p.overBudget || p.underBudget) {
      html += '<div class="cq2-rec-profile">';
      if (hat) html += '<span class="cq2-flag" style="background:#8A5F38">' + esc(hat) + '</span>';
      // Never let an over-budget machine look in-budget at a glance.
      if (p.overBudget) html += '<span class="cq2-flag" style="background:#3B4B3F">' + esc(money(p.overBudget)) + ' over budget</span>';
      // The same for a machine under the band's floor (Dean, 2026-10-21).
      if (p.underBudget) html += '<span class="cq2-flag" style="background:#3B4B3F">' + esc(money(p.underBudget)) + ' below your range</span>';
      html += '</div>';
    }
    if (!isMatch) {
      html += '<p class="cq2-because">Against your match: ' + esc(tradeoffLine(p, best, r.weights)) + '</p>';
    }
    html += '</div>';
    html += listHtml(isMatch ? "Why you'd like it" : 'Why you might prefer it',
      upsides(p, r.weights, 3), 'bq-list--pro');
    html += listHtml('What you give up', downsides(p, r.weights, shown), 'bq-list--con');
    if (p.compare_url) {
      html += '<a class="bq-guide" href="' + esc(p.compare_url) + '">See the full comparison</a>';
    }
    html += disclosureHtml(p);
    html += '</div></div>';

    html += this.wl ? this.wishlistStripHtml() : '';
    html += '<div class="cq2-rec-foot"><div class="cq2-rec-inner">';
    // One row: the buy buttons, then the wishlist heart. No standalone price:
    // it goes inside the button of the store charging it, as on a gear
    // article. "Compare these three" and its sheet were removed (Dean,
    // 2026-10-10) to give the scrolling text more height on a phone; the tabs
    // and the "Against your match" line already carry the difference.
    html += '<div class="bq-foot-row">';
    html += buyButtonsHtml(p);
    if (this.wl) {
      var on = !!this.wl.saved[p.handle];
      html += '<button type="button" class="bq-heart" data-bq-heart aria-pressed="' + (on ? 'true' : 'false') + '" ' +
        'aria-label="Save the ' + esc(p.title) + ' to your wishlist">' +
        '<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="' + HEART_PATH + '"/></svg></button>';
    }
    html += '</div>';
    html += '</div></div>';
    html += '</div>';

    html += this.wl ? this.wishlistSheetHtml(p) : '';

    this.screen('cq2-rec', html, false);

    if (isMatch) {
      var burst = this.stage.querySelector('.cq2-seal-burst');
      for (var i = 0; i < 14; i++) {
        var a = (i / 14) * Math.PI * 2, rad = 62 + (i % 3) * 14, size = 6 + (i % 2) * 3, dot = document.createElement('i');
        dot.style.cssText = 'width:' + size + 'px;height:' + size + 'px;margin:' + (-size / 2) + 'px;background:' + ['#A47449', '#3B4B3F', '#D8B48E'][i % 3] +
          ';--dx:' + Math.round(Math.cos(a) * rad) + 'px;--dy:' + Math.round(Math.sin(a) * rad) + 'px;animation-delay:' + (0.25 + (i % 4) * 0.04) + 's';
        burst.appendChild(dot);
      }
      // The seal and photo animate in on the first draw only, not on every tab switch.
      if (!animate) {
        Array.prototype.forEach.call(this.stage.querySelectorAll('.cq2-seal-disc, .cq2-seal-burst i'), function (el) {
          el.style.animation = 'none';
        });
      }
    }
    if (!animate) {
      var photo = this.stage.querySelector('.cq2-rec-photo');
      if (photo) photo.style.animation = 'none';
    }
    this.placeSeal();

    Array.prototype.forEach.call(this.stage.querySelectorAll('[data-bq-tab]'), function (tab) {
      tab.addEventListener('click', function () {
        var i = parseInt(tab.dataset.bqTab, 10);
        if (i === self.sel) return;
        self.sel = i;
        self.track('brewer_quiz_tab_clicked', { handle: shown[i].handle, position: i + 1 });
        self.renderResultTab(false);
      });
    });
    this.stage.querySelector('[data-bq-rec-back]').addEventListener('click', function () { self.back(); });
    this.stage.querySelector('[data-bq-retake]').addEventListener('click', function () {
      self.track('brewer_quiz_retake_clicked', { best_fit: best.handle });
      self.history = [];
      self.current = 'Q1';
      self.render();
    });
    // Our button keeps the old Continue event name, since it still goes to our
    // product page; a partner's has its own. Both are links, so the event is
    // sent with a beacon rather than holding up the navigation.
    Array.prototype.forEach.call(this.stage.querySelectorAll('[data-bq-buy]'), function (a) {
      a.addEventListener('click', function () {
        var dest = a.getAttribute('data-bq-buy');
        self.track(dest === 'stillwater' ? 'brewer_quiz_continue_clicked' : 'brewer_quiz_partner_clicked', {
          handle: p.handle, position: sel + 1, destination: dest,
          region: document.documentElement.getAttribute('data-gear-region') || 'ca',
          transport_type: 'beacon'
        });
      });
    });
    this.wireWishlist(p, sel + 1);
  };

  // Desktop: the "Your match" badge sits level with the machine's name,
  // straddling the photo's edge. The coffee quiz's placeSeal().
  Quiz.prototype.placeSeal = function () {
    var seal = this.stage.querySelector('.cq2-seal');
    if (!seal) return;
    if (window.innerWidth < 760) { seal.style.top = ''; seal.style.bottom = ''; return; }
    var t = this.stage.querySelector('.cq2-rec-title').getBoundingClientRect();
    var box = this.stage.querySelector('.cq2-rec-img').getBoundingClientRect();
    seal.style.top = Math.round(t.top - box.top + t.height / 2 - seal.offsetHeight / 2) + 'px';
    seal.style.bottom = 'auto';
  };

  // Flat, readable record of what they told us — the retargeting segments are
  // built from these, so keep the keys stable.
  Quiz.prototype.answerSummary = function () {
    var out = {};
    this.history.forEach(function (record) {
      out[record.step] = record.option.label;
    });
    return out;
  };

  // The buttons a gear article shows for this machine (Dean, 2026-10-10),
  // from the `buy` field build_matrix.py writes using gear-retailers.liquid's
  // rules. Canada: ours, plus a paying retailer at the same weight. US: the US
  // retailer or the manufacturer, or ours when the machine has neither. Both
  // sets are rendered and gear-region.js picks the country, as on an article.
  //
  // Each button leads with the price and who charges it ("$1,299 from
  // Stillwater"), never a bare verb.
  var DISCLOSURE = 'Some links to other retailers earn us a commission. It does not change which machines we pick or what you pay.';

  function buySets(product) {
    var buy = product.buy || {};
    // A machine we do not sell gets an article's buttons and nothing of ours.
    if (product.sold === 0) {
      return { ca: buy.ca ? [buy.ca] : [], us: buy.us ? [buy.us] : [] };
    }
    var ours = { label: money(product.price) + ' from Stillwater', url: product.url, ours: true };
    return { ca: buy.ca ? [ours, buy.ca] : [ours], us: [buy.us || ours] };
  }

  // A machine is recommended only to a reader who has somewhere to buy it.
  // Ours always has a button; one we do not sell may have a seller in only one
  // of the two countries (build_matrix.py leaves out a machine with none).
  function buyableIn(products, region) {
    return (products || []).filter(function (p) {
      return buySets(p)[region === 'us' ? 'us' : 'ca'].length > 0;
    });
  }

  // The commission line for a country's buttons, or '' when none of them pays.
  function disclosureText(buttons) {
    var note = '';
    buttons.forEach(function (b) {
      if (b.paid) note = DISCLOSURE + (b.disclosure ? ' ' + b.disclosure : '');
    });
    return note;
  }

  // Two buttons sit side by side beside the heart, so the label breaks after the
  // price: "$1,299.95" over "from 1st in Coffee". One button stays on one line.
  function buyLabelHtml(label) {
    // Only a price leads; "Buy from Amazon" and "See it at Breville" stay whole.
    var m = label.match(/^(\$\S+) ((?:from|at) .+)$/);
    if (!m) return esc(label);
    return '<span class="bq-buy-lead">' + esc(m[1]) + '</span> <span class="bq-buy-from">' + esc(m[2]) + '</span>';
  }

  function buyButtonsHtml(product) {
    var sets = buySets(product);

    function link(b) {
      var dest = b.ours ? 'stillwater' : b.label.replace(/^.* (from|at) /, '');
      return '<a class="cq2-btn cq2-btn--wide bq-buy-btn" href="' + esc(b.url) + '" data-bq-buy="' + esc(dest) + '"' +
        (b.ours ? '' : ' target="_blank" rel="' + (b.paid ? 'sponsored ' : '') + 'nofollow noopener"') +
        '>' + buyLabelHtml(b.label) + '</a>';
    }
    function set(region, buttons) {
      if (!buttons.length) return '';
      var html = '<div class="bq-buy' + (buttons.length > 1 ? ' bq-buy--two' : '') + '" data-gear-region-item="' + region + '">';
      buttons.forEach(function (b) { html += link(b); });
      var note = disclosureText(buttons);
      // On a phone the line moves up into the scrolling text (disclosureHtml)
      // so the pinned bar stays short enough to leave the name in view.
      if (note) html += '<p class="bq-disclosure bq-disclosure--foot">' + esc(note) + '</p>';
      return html + '</div>';
    }

    return set('ca', sets.ca) + set('us', sets.us);
  }

  // The same commission line for the scrolling text, shown on phones only.
  function disclosureHtml(product) {
    var sets = buySets(product);
    var html = '';
    ['ca', 'us'].forEach(function (region) {
      var note = disclosureText(sets[region]);
      if (note) html += '<p class="bq-disclosure bq-disclosure--body" data-gear-region-item="' + region + '">' + esc(note) + '</p>';
    });
    return html;
  }

  function noteHtml(product) {
    var notes = [];
    if (product.needs_grinder) notes.push("You'll need a separate grinder.");
    if (product.plumbable) notes.push('Can be plumbed into a water line.');
    if (!notes.length) return '';
    return '<p class="caption brewer-quiz__note">' + notes.map(esc).join(' ') + '</p>';
  }

  function imageTag(product, shape) {
    var box = shape === 'fill'
      ? 'media brewer-quiz__media-fill global-media-settings'
      : 'media media--square global-media-settings';
    var open = '<div class="' + box + '">';
    if (!product.image) return open + '</div>';
    var src = product.image.indexOf('?') === -1
      ? product.image + '?width=600'
      : product.image + '&width=600';
    return open +
      '<img src="' + esc(src) + '" alt="' + esc(product.title) +
      '" loading="lazy" width="600" height="600">' +
      '</div>';
  }

  function klaviyoPush(args) {
    try {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(args);
    } catch (e) { /* non-blocking */ }
  }

  // ------------------------------------------------------------ analytics ---

  Quiz.prototype.track = function (name, props) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, Object.assign({ event_category: 'brewer_quiz' }, props));
    }
  };

  // --------------------------------------------------------------- utils ---

  // Most answers are a short statement, a full stop, then the reason. Run
  // together on one line they read as a single long sentence, so the statement
  // becomes the answer and the rest becomes the second line. An explicit `sub`
  // always wins; this only splits labels that carry their own explanation.
  function splitAnswer(option) {
    if (option.sub) return { lead: option.label, note: option.sub };
    var m = option.label.match(/^(.+?)\.\s+(.+)$/);
    if (!m) return { lead: option.label, note: null };
    return { lead: m[1], note: m[2] };
  }

  function clamp01(n) { return Math.max(0, Math.min(1, n)); }
  function capitalize(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Exposed so the scoring can be exercised without a DOM — both by the test
  // harness (gear-content/brewer-quiz/test_engine.js) and from the console
  // when tuning weights against the live matrix.
  var api = {
    // Exposed so the tests can assert no two levels share a word.
    scaleWords: function () { return SCALE_WORDS; },
    splitAnswer: splitAnswer,
    buildSteps: buildSteps,
    collect: collect,
    survives: survives,
    score: score,
    pickSpread: pickSpread,
    assignHats: assignHats,
    tradeoffLine: tradeoffLine,
    whyLine: whyLine,
    upsides: upsides,
    downsides: downsides
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.BrewerQuizEngine = api;

  if (typeof document === 'undefined') return;

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bq-root]'), function (root) {
      if (root.dataset.bqReady) return;
      root.dataset.bqReady = '1';
      new Quiz(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
